import React, { useMemo } from 'react';
import { RegionScore } from '../types/ioda';

// Iran provinces SVG paths (low_res) from IranJSON - https://github.com/Javid-Izadfar/IranJSON
import iranProvincesData from '../data/iranProvinces.json';

const VIEWBOX = '0 0 798 724';

type ProvinceEntry = {
  name_en: string;
  name_fa?: string;
  type?: string;
  path: { low_res: string };
};

const iranProvinces = iranProvincesData as Record<string, ProvinceEntry>;

/** Normalize region name for matching (API labels vs IranJSON may differ slightly) */
function normalizeForMatch(name: string): string {
  const n = name.trim().toLowerCase();
  const aliases: Record<string, string> = {
    'kordestan': 'kurdestan',
    'kohgiluyeh and boyer-ahmad': 'kohgiluyeh and buyer ahmad',
    'chaharmahal and bakhtiari': 'chahar mahall and bakhtiari',
    'isfahan': 'esfahan',
    'east azarbaijan': 'east azarbaijan',
    'west azarbaijan': 'west azarbaijan',
  };
  return aliases[n] ?? n;
}

/** Check if region has significant outage (score above threshold or has events) */
const OUTAGE_SCORE_THRESHOLD = 100;

function isRegionAffected(regionName: string, regions: RegionScore[]): boolean {
  const key = normalizeForMatch(regionName);
  const found = regions.find((r) => normalizeForMatch(r.region_name) === key);
  if (!found) return false;
  return found.score > OUTAGE_SCORE_THRESHOLD || found.event_cnt > 0;
}

/** Get traffic-based fill (blue gradient 0–100%) */
function trafficToFill(pct: number): string {
  if (!Number.isFinite(pct) || pct <= 0) return '#1E3A5F';
  const t = Math.min(100, pct) / 100;
  /* Low: darker navy · High: bright sky — wider contrast for choropleth scan */
  const low = { r: 30, g: 58, b: 95 };
  const high = { r: 56, g: 189, b: 248 };
  const r = Math.round(low.r + (high.r - low.r) * t);
  const g = Math.round(low.g + (high.g - low.g) * t);
  const b = Math.round(low.b + (high.b - low.b) * t);
  return `rgb(${r},${g},${b})`;
}

export type TrafficByRegionItem = { regionName: string; regionCode?: string; value: number };

interface IranMapProps {
  regionData: RegionScore[];
  /** When set, provinces are colored by traffic % (blue gradient) instead of outage. */
  trafficData?: TrafficByRegionItem[];
  isFa?: boolean;
  className?: string;
}

export const IranMap: React.FC<IranMapProps> = ({
  regionData,
  trafficData,
  isFa = false,
  className = '',
}) => {
  const fillByProvince = useMemo(() => {
    const map: Record<string, string> = {};
    Object.entries(iranProvinces).forEach(([key, prov]) => {
      if (!prov.path?.low_res) return;
      if (trafficData && trafficData.length > 0) {
        const item = trafficData.find((t) => normalizeForMatch(t.regionName) === normalizeForMatch(prov.name_en));
        map[key] = item ? trafficToFill(item.value) : '#1e3a5f';
      } else {
        const affected = isRegionAffected(prov.name_en, regionData);
        map[key] = affected ? 'rgba(220, 38, 38, 0.7)' : '#22c55e';
      }
    });
    return map;
  }, [regionData, trafficData]);

  return (
    <div className={`${className}`}>
      <svg
        viewBox={VIEWBOX}
        className="w-full block"
        style={{ maxHeight: 420, minHeight: 300 }}
        preserveAspectRatio="xMidYMid meet"
      >
        {Object.entries(iranProvinces).map(([key, prov]) => {
          if (!prov.path?.low_res) return null;
          const fill = fillByProvince[key] ?? '#22c55e';
          return (
            <path
              key={key}
              d={prov.path.low_res}
              fill={fill}
              stroke="#374151"
              strokeWidth={0.5}
              className="transition-colors duration-300 hover:opacity-80"
              style={{ cursor: 'default' }}
            >
              <title>
                {(isFa && prov.name_fa) ? prov.name_fa : prov.name_en}
                {trafficData?.length
                  ? ` — ${(trafficData.find((t) => normalizeForMatch(t.regionName) === normalizeForMatch(prov.name_en))?.value ?? 0).toFixed(1)}%`
                  : regionData.length > 0
                    ? ` — ${isRegionAffected(prov.name_en, regionData) ? (isFa ? 'تحت تأثیر' : 'Affected') : (isFa ? 'سالم' : 'OK')}`
                    : ''}
              </title>
            </path>
          );
        })}
      </svg>
      <div
        className="mt-3 flex flex-wrap justify-center gap-6 font-sans text-[11px]"
        style={{ color: 'var(--text-secondary)' }}
      >
        {!trafficData?.length ? (
          <>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" aria-hidden />
              {isFa ? 'عادی' : 'Normal'}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 shrink-0 rounded-full bg-[rgba(220,38,38,0.7)]" aria-hidden />
              {isFa ? 'تحت تأثیر' : 'Disrupted'}
            </span>
          </>
        ) : (
          <>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm" style={{ background: '#1e3a5f' }} />
              {isFa ? 'کم' : 'Low'}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-sky-400" />
              {isFa ? 'زیاد' : 'High'}
            </span>
          </>
        )}
      </div>
    </div>
  );
};
